import React, { useContext, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { bemJoin } from 'bem-join'
import { IMessage, LetterRequest, ModernComment, PageLoader, Stack } from 'component-library-next'
import { hasPath } from 'ramda'

import { Features } from '@app/Feature'
import { isFeatureEnabled } from '@app/Feature/Store/selectors'
import { CaseState, CustomerDocument, IdentifiedClause } from '@app/graphql/types'
import { ModernChat } from '@app/lib/components/ModernChat'
import { DocumentsList } from '@app/lib/display/DocumentsList/DocumentsList'
import { MissedPhoneAppointment } from '@app/modules/Messages/Components/MissedPhoneAppointment/MissedPhoneAppointment'
import { OpenAllDocuments } from '@app/modules/Messages/Components/OpenAllDocuments/OpenAllDocuments'
import { enrichMessageContent } from '@app/modules/Messages/Components/Question/helpers/enrichMessageContent'
import { prepareMessageBadges } from '@app/modules/Messages/Components/Question/helpers/prepareMessageBadges'
import { PowerOfAttorneyRequest } from '@app/modules/Messages/Components/Question/PowerOfAttorneyRequest'
import { ResultPageLink } from '@app/modules/Messages/Components/Question/ResultPageLink/ResultPageLink'
import { clauseTypeTranslations } from '@app/modules/Messages/Components/RenovationClauses'
import { RenovationClauses } from '@app/modules/Messages/Components/RenovationClauses/RenovationClauses'
import { SuggestPhoneAppointment } from '@app/modules/Messages/Components/SuggestPhoneAppointment/SuggestPhoneAppointment'
import { UtilityBillAIContainer } from '@app/modules/Messages/Components/UtilityBillAICheckResults/UtilityBillAIContainer'
import { letterRequestContext } from '@app/modules/Messages/letterRequestContext'
import { refetchCustomerAsLawyerQuery } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { Customer, Message } from '@app/queries/CustomerAsLawyer/models'
import { refetchLetterRequestsForCustomerQuery } from '@app/queries/LetterRequestsForCustomer/LetterRequestsForCustomer.queries'
import { useOpenLetterRequestMutation } from '@app/queries/OpenLetterRequest/OpenLetterRequest.queries'

import './Question.scss'

type Props = {
  message: Message
  customer: Customer
  isLatestQuestion: boolean
}

export const Question = ({ customer, message, isLatestQuestion }: Props) => {
  const { letterRequestData, letterLoading } = useContext(letterRequestContext)

  const scrollRef = useRef<HTMLDivElement>(null)
  const letterRequestChecked = !!message.letterRequest?.id
  const documents = message.documents as CustomerDocument[]

  const b = bemJoin('dialog-item__customer')

  const powerOfAttorneyEnabled = useSelector(isFeatureEnabled(Features.POWER_OF_ATTORNEY))
  const messageApprovedButNotAnswered = message.case?.state === CaseState.Approved

  const showSuggestAppointment =
    useSelector(isFeatureEnabled(Features.SUGGEST_APPOINTMENT)) &&
    messageApprovedButNotAnswered &&
    !message.appointmentAt

  const showMissedAppointment = messageApprovedButNotAnswered && message.appointmentAt

  useEffect(() => {
    // Scroll to this component in case its the last one
    if (isLatestQuestion) {
      scrollRef.current && scrollRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [scrollRef])

  const [openLetterRequest, { loading: openingLetterRequest }] = useOpenLetterRequestMutation()

  const openNewLetterRequest = async () => {
    await openLetterRequest({
      refetchQueries: [
        refetchLetterRequestsForCustomerQuery({ customerId: customer.id }),
        refetchCustomerAsLawyerQuery({ customerId: customer.id })
      ],
      variables: {
        questionId: message.id
      }
    })
  }

  const getSortedInvalidClauses = (clauses: IdentifiedClause[]): IdentifiedClause[] => {
    return clauses.filter(
      (clause) => !clause.effective && Object.keys(clauseTypeTranslations).includes(clause.type)
    )
  }

  const canOpenNewRequest = letterRequestData?.letterAllowance?.canRequestLetter
  const lawyerCanOpenLetterRequest = isLatestQuestion && canOpenNewRequest

  const hasComment = hasPath(['serviceComment', 'comment'], message)

  const displayRenovationClauses = useSelector(isFeatureEnabled(Features.RENOVATION_CLAUSE_DISPLAY))

  const showLoader = openingLetterRequest || letterLoading

  const displayLetterRequest = letterRequestChecked || lawyerCanOpenLetterRequest

  const messageWithoutDocuments = {
    ...message,
    documents: []
  }

  const badges = prepareMessageBadges(message)

  return (
    <div className={b()} ref={isLatestQuestion ? scrollRef : undefined}>
      <Stack>
        {showLoader && <PageLoader loading />}
        {showSuggestAppointment && <SuggestPhoneAppointment messageId={message.id} />}
        {showMissedAppointment && <MissedPhoneAppointment messageId={message.id} />}
        <ModernChat
          badges={badges}
          collapsesLongText={!isLatestQuestion}
          displayLetterRequest={false}
          message={enrichMessageContent({ customer, message: messageWithoutDocuments }) as IMessage}
        >
          <ResultPageLink insuranceClaim={message.case?.insuranceClaim} />
          {displayLetterRequest && (
            <LetterRequest
              checked={letterRequestChecked}
              disabled={!lawyerCanOpenLetterRequest}
              onChange={openNewLetterRequest}
              text={'Anwaltsschreiben anfertigen'}
            />
          )}
          {!!powerOfAttorneyEnabled && letterRequestChecked && (
            <PowerOfAttorneyRequest letterRequest={message.letterRequest} />
          )}
          {hasComment && message.serviceComment?.comment && (
            <ModernComment comment={hasComment && message.serviceComment?.comment} />
          )}
          {documents && documents.length > 1 && (
            <div className={b('open-all-documents')}>
              <OpenAllDocuments documents={documents} />
            </div>
          )}
          {documents && <DocumentsList documents={documents as CustomerDocument[]} />}
        </ModernChat>
        <UtilityBillAIContainer documents={documents} />
        {!!displayRenovationClauses &&
          documents
            .filter((doc) => doc.identifiedClauses != null)
            .map((doc) => {
              const sortedInvalidClauses = getSortedInvalidClauses(doc.identifiedClauses || [])

              if (sortedInvalidClauses.length === 0) {
                return null
              }

              return <RenovationClauses doc={doc} clauses={sortedInvalidClauses} />
            })}
      </Stack>
    </div>
  )
}
